import PersonWaitingIcon from '@components/Icon/PersonWaiting';
import AvatarWithStatus, { AvatarWithStatusProps } from '@modules/talents/components/AvatarWithStatus';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Popper,
    SxProps,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { useMutation, useApolloClient } from '@apollo/client';
import { UPDATE_PROFILE_PICTURE_TALENT } from '@gql/talents';
import { FavoriteToggle } from './FavoriteToggle';

type SelectableStatus = Exclude<AvatarWithStatusProps['status'], 'mixed'>;

export interface AvatarStatusFavoriteProps {
    talentIds: string | string[];
    currentStatus: AvatarWithStatusProps['status'];
    onStatusChange?: (status: SelectableStatus) => void;
    image?: string;
    alt?: string;
    disabled?: boolean;
    loading?: boolean;
    shouldStopPropagation?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    showStatus?: boolean;
    /** Talent's upercutFavorites array from GraphQL query (single talent) or map of talent ID to favorites (multiple talents) */
    upercutFavorites?: Array<{
        _id: string;
        type: string;
        upercutId?: string;
        createdAt: string;
    }> | Record<string, Array<{
        _id: string;
        type: string;
        upercutId?: string;
        createdAt: string;
    }>>;
    /** Count of talents already in the public favorites list */
    publicFavoriteCount?: number;
    /** Count of talents already in the private favorites list */
    privateFavoriteCount?: number;
    favoriteLoading?: boolean;
    statusOptions?: SelectableStatus[];
    sx?: SxProps<Theme>;
    avatarSx?: SxProps<Theme>;
    /** When true, clicking the avatar image opens the profile picture upload dialog */
    allowAvatarEdit?: boolean;
}

const DEFAULT_STATUS_OPTIONS: SelectableStatus[] = ['nothing', 'looking', 'open', 'closed', 'inactive'];

// ─── Image crop helpers ────────────────────────────────────────────────────────

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not create canvas context');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height,
    );
    return new Promise((resolve) => {
        canvas.toBlob((blob) => { if (blob) resolve(blob); }, 'image/jpeg', 0.95);
    });
};

// ─── Dropzone ─────────────────────────────────────────────────────────────────

const DropzoneArea: React.FC<{ onDrop: (file: File) => void }> = ({ onDrop }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (accepted) => { if (accepted.length > 0) onDrop(accepted[0]); },
        accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
        multiple: false,
    });
    return (
        <Box
            {...getRootProps()}
            sx={(theme) => ({
                width: '100%',
                height: 200,
                border: '2px dashed',
                borderColor: isDragActive ? theme.palette.primary.main : theme.palette.divider,
                borderRadius: '8px',
                backgroundColor: isDragActive ? theme.palette.action.hover : theme.palette.background.paper,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                transition: 'all 0.2s ease',
            })}
        >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" align="center">
                {isDragActive
                    ? 'Déposez la photo ici...'
                    : 'Glissez-déposez une photo ou cliquez pour sélectionner'}
            </Typography>
        </Box>
    );
};

// ─── Main component ────────────────────────────────────────────────────────────

const AvatarStatusFavorite: React.FC<AvatarStatusFavoriteProps> = ({
    talentIds: talentId,
    currentStatus,
    onStatusChange,
    image,
    alt,
    disabled = false,
    loading = false,
    shouldStopPropagation = false,
    size = 'sm',
    showStatus = true,
    upercutFavorites = [],
    publicFavoriteCount = 0,
    privateFavoriteCount = 0,
    favoriteLoading = false,
    statusOptions = DEFAULT_STATUS_OPTIONS,
    sx,
    avatarSx,
    allowAvatarEdit = false,
}) => {
    const apolloClient = useApolloClient();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);

    // ── avatar upload state ────────────────────────────────────────────────────
    const [uploadOpen, setUploadOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [localImage, setLocalImage] = useState<string | undefined>(image);

    const [updateProfilePicture, { loading: uploading }] = useMutation(UPDATE_PROFILE_PICTURE_TALENT);

    const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleFileDrop = (file: File) => {
        if (!file.type.startsWith('image/')) {
            setUploadError('Le fichier doit être une image');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result as string);
            setUploadError(null);
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!imageSrc || !croppedAreaPixels) return;
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const croppedFile = new File([croppedBlob], 'profile.jpg', { type: 'image/jpeg' });
            const talentIdStr = Array.isArray(talentId) ? talentId[0] : talentId;

            const { data } = await updateProfilePicture({
                variables: { talentId: talentIdStr, file: croppedFile },
            });

            const newUrl: string | undefined = data?.updateProfilePictureTalent;
            if (newUrl) {
                setLocalImage(newUrl);
                apolloClient.cache.modify({
                    id: apolloClient.cache.identify({ __typename: 'Talent', _id: talentIdStr }),
                    fields: {
                        profilePicture: (existing: any) => ({ ...existing, url: newUrl }),
                        logo: () => newUrl,
                    },
                });
            }

            closeUploadDialog();
        } catch (err: any) {
            setUploadError(err?.message || 'Échec du téléchargement de la photo');
        }
    };

    const closeUploadDialog = () => {
        setUploadOpen(false);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setUploadError(null);
    };

    const handleAvatarClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (shouldStopPropagation) e.stopPropagation();
        if (!allowAvatarEdit) return;
        setUploadOpen(true);
    };

    // ── status popup ──────────────────────────────────────────────────────────

    const handleToggle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (shouldStopPropagation) e.stopPropagation();
        if (!disabled) setOpen(prev => !prev);
    };

    const handleStatusSelect = (status: SelectableStatus) => {
        onStatusChange?.(status);
        setOpen(false);
    };

    const getTopPosition = (size: AvatarWithStatusProps['size']) => {
        switch (size) {
            case 'xl':
                return '8px';
            case 'lg':
                return '6px';
            case 'md':
                return '5px';
            case 'sm':
            default:
                return '4px';
        }
    };

    const getAvatarSize = (avatarSize: AvatarStatusFavoriteProps['size']) => {
        switch (avatarSize) {
            case 'xl': return 140;
            case 'lg': return 102;
            case 'md': return 50;
            case 'sm':
            default: return 42;
        }
    };

    const effectiveImage = localImage ?? image;

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    width: 'fit-content',
                    paddingTop: '8px',
                    paddingRight: '12px',
                    ...sx,
                }}
            >
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress size={16} />
                    </Box>
                )}
                {showStatus ? (
                    <ClickAwayListener
                        onClickAway={event => {
                            if (shouldStopPropagation) event.stopPropagation();
                            setOpen(false);
                        }}
                    >
                        <Box>
                            <Box ref={anchorRef} sx={{ position: 'relative' }}>
                                <AvatarWithStatus
                                    size={size}
                                    status={currentStatus}
                                    src={effectiveImage}
                                    alt={alt}
                                    sx={{
                                        flex: 'none',
                                        cursor: disabled ? 'default' : 'pointer',
                                        ...avatarSx,
                                    }}
                                    onClick={handleToggle}
                                    onAvatarClick={allowAvatarEdit ? handleAvatarClick : undefined}
                                />
                                {allowAvatarEdit && (
                                    <Tooltip title="Modifier la photo">
                                        <Box
                                            onClick={(e) => { e.stopPropagation(); setUploadOpen(true); }}
                                            sx={{
                                                position: 'absolute',
                                                top: `-${getTopPosition(size)}`,
                                                left: 0,
                                                width: '100%',
                                                height: `${getAvatarSize(size)}px`,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(0,0,0,0.4)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: 0,
                                                cursor: 'pointer',
                                                transition: 'opacity 0.2s ease',
                                                mt: '8px',
                                                '&:hover': { opacity: 1 },
                                            }}
                                        >
                                            <EditIcon sx={{ color: '#fff', fontSize: size === 'xl' ? 32 : 20 }} />
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                            <Popper
                                open={open}
                                anchorEl={anchorRef.current}
                                placement="bottom-start"
                                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                                sx={{ zIndex: 1300 }}
                            >
                                <Box
                                    sx={{
                                        borderRadius: '16px',
                                        background: '#282C30',
                                        border: '1px solid #222426',
                                        boxShadow: '0 16px 40px 0 rgba(18, 31, 44, 0.20)',
                                        padding: '6px',
                                        display: 'flex',
                                        gap: '16px',
                                    }}
                                >
                                    {statusOptions.map(status => (
                                        <Box
                                            key={status}
                                            sx={{
                                                background: currentStatus === status ? '#222426' : 'transparent',
                                                borderRadius: '8px',
                                                padding: '4px 4px 2px',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s',
                                                '&:hover': {
                                                    background: currentStatus === status ? '#222426' : 'rgba(255, 255, 255, 0.05)',
                                                },
                                            }}
                                        >
                                            <AvatarWithStatus
                                                status={status}
                                                src={effectiveImage}
                                                onClick={event => {
                                                    if (shouldStopPropagation) event.stopPropagation();
                                                    handleStatusSelect(status);
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Popper>
                        </Box>
                    </ClickAwayListener>
                ) : (
                    <Avatar
                        alt={alt}
                        src={effectiveImage}
                        sx={{
                            width: getAvatarSize(size),
                            height: getAvatarSize(size),
                            ...avatarSx,
                        }}
                    />
                )}
                <FavoriteToggle
                    upercutFavorites={upercutFavorites}
                    publicCount={publicFavoriteCount}
                    privateCount={privateFavoriteCount}
                    loading={favoriteLoading}
                    talentIds={talentId}
                    size={size}
                />

            </Box>

            {/* Profile picture upload dialog */}
            <Dialog open={uploadOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Modifier la photo de profil</DialogTitle>
                <DialogContent>
                    {!imageSrc ? (
                        <DropzoneArea onDrop={handleFileDrop} />
                    ) : (
                        <Box sx={{ position: 'relative', width: '100%', height: 360 }}>
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </Box>
                    )}
                    {uploadError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {uploadError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    {imageSrc && (
                        <Button onClick={() => setImageSrc(null)} disabled={uploading}>
                            Choisir une autre photo
                        </Button>
                    )}
                    <Button onClick={closeUploadDialog} disabled={uploading} variant="text" color="secondary">
                        Annuler
                    </Button>
                    {imageSrc && (
                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={uploading || !croppedAreaPixels}
                            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {uploading ? 'Téléchargement...' : 'Enregistrer'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AvatarStatusFavorite;
